import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts';

import { cssVar } from '@feathr/hooks';

import {
  formatAxisDate,
  formatCurrencyTooltipValue,
  type IDonationAmountChartRow,
} from './DonationFormTrends.utils';
import type { ITrendChartLegendEntry } from './TrendChartFrame';
import TrendChartFrame from './TrendChartFrame';

export interface IDonationAmountTrendChartProps {
  /** Chart-ready rows produced by `buildDonationAmountChartData`. */
  data: IDonationAmountChartRow[];
}

/**
 * Donation Amount Trend — recharts BarChart with two grouped vertical bars
 * per day (previous + current). Both bars are solid: current is sky-600 and
 * previous is neutral-500 per Figma node 10162:10993.
 */
function DonationAmountTrendChart({ data }: Readonly<IDonationAmountTrendChartProps>): JSX.Element {
  const { t } = useTranslation();
  const currentColor = cssVar('--color-sky-600');
  const previousColor = cssVar('--color-neutral-500');

  const legend: ITrendChartLegendEntry[] = [
    { label: t('Current Period'), color: currentColor },
    { label: t('Previous Period'), color: previousColor },
  ];

  return (
    <TrendChartFrame
      dataName={'donation-amount-trend-chart'}
      legend={legend}
      legendDataName={'donation-amount-trend-legend'}
      subtitle={t('Total donation amounts over time')}
      title={t('Donation Amount Trend')}
    >
      <BarChart barGap={4} data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid
          stroke={cssVar('--color-decorative-secondary')}
          strokeDasharray={'4 4'}
          vertical={false}
        />
        <XAxis
          axisLine={false}
          dataKey={'date'}
          interval={'preserveStartEnd'}
          minTickGap={24}
          tickFormatter={formatAxisDate}
          tickLine={false}
        />
        <YAxis axisLine={false} tickLine={false} width={48} />
        <Tooltip formatter={formatCurrencyTooltipValue} labelFormatter={formatAxisDate} />
        <Bar
          dataKey={'amountPrevious'}
          fill={previousColor}
          maxBarSize={24}
          name={t('Donation amount (Previous)')}
        />
        <Bar
          dataKey={'amountCurrent'}
          fill={currentColor}
          maxBarSize={24}
          name={t('Donation amount (Current)')}
        />
      </BarChart>
    </TrendChartFrame>
  );
}

export default DonationAmountTrendChart;
